<template>
  <section class="detail-page">
    <button type="button" class="detail-back" @click="goBack">
      <van-icon name="arrow-left" size="20" />
    </button>

    <div v-if="loading" class="state-wrap">
      <van-loading size="24px" vertical>加载中...</van-loading>
    </div>

    <div v-else-if="error" class="state-wrap">
      <div class="state-card">
        <div class="state-card__title">详情加载失败</div>
        <div class="state-card__desc">{{ error }}</div>
        <van-button round type="primary" @click="loadDetail">重试</van-button>
      </div>
    </div>

    <template v-else-if="detail">
      <div class="detail-hero">
        <van-image width="100%" height="100%" fit="cover" :src="resolveVideoCover(detail)" />
        <van-button
          class="detail-hero__breakdown"
          size="small"
          round
          type="primary"
          :disabled="!coverSourceText"
          @click.stop="openCoverBreakdown"
        >
          拆解
        </van-button>
      </div>

      <div class="detail-intro">
        <div class="detail-intro__eyebrow">{{ detail.channelName || detail.uploaderName || 'YouTube' }}</div>
        <div class="detail-title-row">
          <h1
            :class="['detail-intro__title', { 'detail-intro__title--breakdown': titleBreakdown }]"
            @click="handleTitleClick"
          >
            {{ titleText || '-' }}
          </h1>
          <van-button
            size="small"
            round
            plain
            type="primary"
            :disabled="!titleText"
            @click="openTitleBreakdown"
          >
            拆解
          </van-button>
        </div>
        <div v-if="detail.titleCn && detail.titleCn !== detail.title" class="detail-intro__subtitle">
          {{ detail.title || '-' }}
        </div>
      </div>

      <div class="meta-grid">
        <article class="meta-card">
          <span class="meta-card__label">播放量</span>
          <strong>{{ formatViewCount(detail.viewCount) }}</strong>
        </article>
        <article class="meta-card">
          <span class="meta-card__label">发布日期</span>
          <strong>{{ formatDate(detail.publishedAt) }}</strong>
        </article>
      </div>

      <div class="detail-action-grid">
        <section class="action-card">
          <div class="action-card__body">
            <span class="action-card__label">领域分类</span>
            <strong>{{ domainCategoryName }}</strong>
            <p>{{ domainBreakdowns.length ? `${domainBreakdowns.length} 条拆解` : '暂无拆解' }}</p>
          </div>
          <van-button
            size="small"
            round
            plain
            type="primary"
            :disabled="!hasDomainCategory"
            @click="openDomainBreakdownSheet"
          >
            拆解
          </van-button>
        </section>

        <section class="action-card">
          <div class="action-card__body">
            <span class="action-card__label">随笔</span>
            <strong>全局随笔</strong>
            <p>{{ globalEssays.length ? `${globalEssays.length} 条随笔` : '暂无随笔' }}</p>
          </div>
          <van-button
            size="small"
            round
            plain
            type="primary"
            @click="openEssaySheet"
          >
            随笔
          </van-button>
        </section>
      </div>

      <section class="info-card">
        <div class="info-card__label">视频简介</div>
        <SelectableBreakdownText
          v-if="descriptionText"
          class="info-card__content"
          :text="descriptionText"
          :breakdowns="descriptionBreakdowns"
          source-type="description_selection"
          key-prefix="description"
          default-breakdown-type="description"
          overlap-message="该简介区间已存在拆解，不能重复新增"
          @create-breakdown="openSelectionBreakdown"
          @edit-breakdown="openEditBreakdown"
        />
        <div v-else class="info-card__content">-</div>
      </section>

      <section class="transcript-card">
        <div class="transcript-card__label">中文字幕全文</div>
        <SelectableBreakdownText
          v-if="zhSubtitle"
          class="transcript-card__content"
          :text="zhSubtitle"
          :breakdowns="subtitleBreakdowns"
          source-type="subtitle_selection"
          key-prefix="subtitle"
          overlap-message="该字幕区间已存在拆解，不能重复新增"
          @create-breakdown="openSelectionBreakdown"
          @edit-breakdown="openEditBreakdown"
        />
        <div v-else class="transcript-card__empty">暂无中文字幕全文</div>
      </section>

      <YouTubeBreakdownSheet
        v-model:show="sheetState.visible"
        :mode="sheetState.mode"
        :source-type="sheetState.sourceType"
        :source-text="sheetState.sourceText"
        :breakdown-type="sheetState.breakdownType"
        :enabled-breakdown-types="sheetState.enabledBreakdownTypes"
        :initial-breakdown-content="sheetState.initialBreakdownContent"
        :review="sheetState.review"
        :saving="sheetState.saving"
        @save="handleSaveBreakdown"
        @delete="handleDeleteBreakdown"
        @closed="handleSheetClosed"
      />

      <van-popup
        v-model:show="domainSheet.visible"
        closeable
        close-icon-position="top-right"
        position="bottom"
        :style="{ height: '100%' }"
        @closed="resetDomainForm"
      >
        <div class="text-sheet">
          <div class="text-sheet__body">
            <div class="text-sheet__header">
              <div>
                <div class="text-sheet__eyebrow">领域分类</div>
                <h2>{{ domainCategoryName }}</h2>
              </div>
              <van-button
                size="small"
                round
                type="primary"
                :disabled="domainForm.saving"
                @click="openDomainCreateForm"
              >
                新增
              </van-button>
            </div>

            <div v-if="domainBreakdowns.length" class="text-sheet__list">
              <article
                v-for="item in domainBreakdowns"
                :key="item.id"
                class="text-item"
              >
                <div class="text-item__header">
                  <strong>拆解内容</strong>
                  <div class="text-item__actions">
                    <van-button size="mini" plain type="primary" :disabled="domainForm.saving" @click="openDomainEditForm(item)">
                      编辑
                    </van-button>
                    <van-button size="mini" plain type="danger" :disabled="domainForm.saving" @click="deleteDomainBreakdown(item)">
                      删除
                    </van-button>
                  </div>
                </div>
                <div class="text-item__content">{{ item.breakdownContent }}</div>
              </article>
            </div>
            <van-empty v-else description="暂无领域分类拆解" />

            <div v-if="domainForm.visible" class="text-sheet__form">
              <div class="text-sheet__label">拆解内容</div>
              <van-cell-group inset>
                <van-field
                  v-model="domainForm.breakdownContent"
                  rows="7"
                  autosize
                  type="textarea"
                  maxlength="5000"
                  show-word-limit
                  placeholder="输入拆解内容"
                />
              </van-cell-group>
            </div>
          </div>

          <div v-if="domainForm.visible" class="text-sheet__actions">
            <van-button round plain :disabled="domainForm.saving" @click="resetDomainForm">取消</van-button>
            <van-button round type="primary" :loading="domainForm.saving" @click="saveDomainBreakdown">保存</van-button>
          </div>
        </div>
      </van-popup>

      <van-popup
        v-model:show="essaySheet.visible"
        closeable
        close-icon-position="top-right"
        position="bottom"
        :style="{ height: '100%' }"
        @closed="resetEssayForm"
      >
        <div class="text-sheet">
          <div class="text-sheet__body">
            <div class="text-sheet__header">
              <div>
                <div class="text-sheet__eyebrow">随笔</div>
                <h2>全局随笔</h2>
              </div>
              <van-button
                size="small"
                round
                type="primary"
                :disabled="essayForm.saving"
                @click="openEssayCreateForm"
              >
                新增
              </van-button>
            </div>

            <div v-if="globalEssays.length" class="text-sheet__list">
              <article
                v-for="item in globalEssays"
                :key="item.id"
                class="text-item"
              >
                <div class="text-item__header">
                  <strong>随笔内容</strong>
                  <div class="text-item__actions">
                    <van-button size="mini" plain type="primary" :disabled="essayForm.saving" @click="openEssayEditForm(item)">
                      编辑
                    </van-button>
                    <van-button size="mini" plain type="danger" :disabled="essayForm.saving" @click="deleteEssay(item)">
                      删除
                    </van-button>
                  </div>
                </div>
                <div class="text-item__content">{{ item.content }}</div>
              </article>
            </div>
            <van-empty v-else description="暂无随笔" />

            <div v-if="essayForm.visible" class="text-sheet__form">
              <div class="text-sheet__label">随笔内容</div>
              <van-cell-group inset>
                <van-field
                  v-model="essayForm.content"
                  rows="7"
                  autosize
                  type="textarea"
                  maxlength="5000"
                  show-word-limit
                  placeholder="输入随笔内容"
                />
              </van-cell-group>
            </div>
          </div>

          <div v-if="essayForm.visible" class="text-sheet__actions">
            <van-button round plain :disabled="essayForm.saving" @click="resetEssayForm">取消</van-button>
            <van-button round type="primary" :loading="essayForm.saving" @click="saveEssay">保存</van-button>
          </div>
        </div>
      </van-popup>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { showConfirmDialog, showToast } from 'vant'
import { useRoute, useRouter } from 'vue-router'

import SelectableBreakdownText from '../components/SelectableBreakdownText.vue'
import YouTubeBreakdownSheet from '../components/YouTubeBreakdownSheet.vue'
import {
  deleteDomainCategoryBreakdown,
  deleteGlobalEssay,
  deleteYoutubeBreakdown,
  fetchYoutubeDetail,
  saveDomainCategoryBreakdown,
  saveGlobalEssay,
  saveYoutubeBreakdown,
} from '../api/youtube'
import { formatDate, formatViewCount, getZhSubtitle, resolveVideoCover } from '../utils/youtube'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const error = ref('')
const detail = ref(null)
const sheetState = reactive({
  visible: false,
  mode: 'create',
  saving: false,
  breakdownId: 0,
  sourceType: 'subtitle_selection',
  sourceText: '',
  sourceStartOffset: null,
  sourceEndOffset: null,
  breakdownType: 'body',
  enabledBreakdownTypes: ['body', 'hook', 'opening', 'ending'],
  initialBreakdownContent: '',
  review: null,
})
const domainSheet = reactive({
  visible: false,
})
const domainForm = reactive({
  visible: false,
  saving: false,
  mode: 'create',
  breakdownId: 0,
  breakdownContent: '',
})
const essaySheet = reactive({
  visible: false,
})
const essayForm = reactive({
  visible: false,
  saving: false,
  mode: 'create',
  essayId: 0,
  content: '',
})

const videoId = computed(() => Number.parseInt(String(route.query.id || ''), 10) || 0)
const titleText = computed(() => String(
  detail.value?.titleCn || detail.value?.title || detail.value?.videoId || '',
).trim())
const descriptionText = computed(() => String(detail.value?.descriptionCn || '').trim())
const zhSubtitle = computed(() => getZhSubtitle(detail.value))
const breakdowns = computed(() => Array.isArray(detail.value?.breakdowns) ? detail.value.breakdowns : [])
const titleBreakdown = computed(() => breakdowns.value.find((item) => item.sourceType === 'title') || null)
const coverBreakdown = computed(() => breakdowns.value.find((item) => (
  item.sourceType === 'cover' && item.breakdownType === 'cover'
)) || null)
const coverSourceText = computed(() => String(detail.value?.coverUrl || detail.value?.coverSourceUrl || '').trim())
const domainBreakdowns = computed(() => Array.isArray(detail.value?.domainCategoryBreakdowns)
  ? detail.value.domainCategoryBreakdowns
  : [])
const globalEssays = computed(() => Array.isArray(detail.value?.globalEssays) ? detail.value.globalEssays : [])
const hasDomainCategory = computed(() => Number(detail.value?.domainCategoryId || 0) > 0)
const domainCategoryName = computed(() => (
  hasDomainCategory.value ? String(detail.value?.domainCategoryName || `#${detail.value?.domainCategoryId}`) : '未分类'
))
const descriptionBreakdowns = computed(() => breakdowns.value
  .filter((item) => item.sourceType === 'description_selection')
  .sort((left, right) => Number(left.sourceStartOffset || 0) - Number(right.sourceStartOffset || 0)))
const subtitleBreakdowns = computed(() => breakdowns.value
  .filter((item) => item.sourceType === 'subtitle_selection')
  .sort((left, right) => Number(left.sourceStartOffset || 0) - Number(right.sourceStartOffset || 0)))

onMounted(() => {
  loadDetail()
})

async function loadDetail(options = {}) {
  if (!videoId.value) {
    error.value = '缺少视频 ID'
    loading.value = false
    return
  }

  const silent = options.silent === true
  if (!silent) {
    loading.value = true
  }
  error.value = ''

  try {
    detail.value = await fetchYoutubeDetail(videoId.value)
  } catch (loadError) {
    error.value = loadError?.message || '加载失败'
  } finally {
    if (!silent) {
      loading.value = false
    }
  }
}

async function refreshDetailPreservingScroll() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  await loadDetail({ silent: true })
  requestAnimationFrame(() => {
    window.scrollTo({
      top: scrollTop,
      left: 0,
      behavior: 'auto',
    })
  })
}

function goBack() {
  const query = {}
  if (route.query.channelId) {
    query.channelId = String(route.query.channelId)
  }
  if (route.query.tag) {
    query.tag = String(route.query.tag)
  }
  router.push({
    name: 'youtube-list',
    query,
  })
}

function resetSheetState() {
  sheetState.visible = false
  sheetState.mode = 'create'
  sheetState.saving = false
  sheetState.breakdownId = 0
  sheetState.sourceType = 'subtitle_selection'
  sheetState.sourceText = ''
  sheetState.sourceStartOffset = null
  sheetState.sourceEndOffset = null
  sheetState.breakdownType = 'body'
  sheetState.enabledBreakdownTypes = ['body', 'hook', 'opening', 'ending']
  sheetState.initialBreakdownContent = ''
  sheetState.review = null
}

function resolveEnabledBreakdownTypes(sourceType) {
  if (sourceType === 'cover') return ['cover']
  if (sourceType === 'title') return ['title_cover']
  if (sourceType === 'subtitle_selection') return ['body', 'hook', 'opening', 'ending']
  if (sourceType === 'description_selection') return ['description']
  return ['body']
}

function resolveDefaultBreakdownType(sourceType) {
  if (sourceType === 'cover') return 'cover'
  if (sourceType === 'title') return 'title_cover'
  if (sourceType === 'description_selection') return 'description'
  return 'body'
}

function handleSheetClosed() {
  if (!sheetState.saving) {
    resetSheetState()
  }
}

function openTitleBreakdown() {
  if (!titleText.value) return
  sheetState.visible = true
  sheetState.mode = titleBreakdown.value ? 'edit' : 'create'
  sheetState.breakdownId = Number(titleBreakdown.value?.id || 0)
  sheetState.sourceType = 'title'
  sheetState.sourceText = titleText.value
  sheetState.sourceStartOffset = null
  sheetState.sourceEndOffset = null
  sheetState.breakdownType = 'title_cover'
  sheetState.enabledBreakdownTypes = resolveEnabledBreakdownTypes(sheetState.sourceType)
  sheetState.initialBreakdownContent = String(titleBreakdown.value?.breakdownContent || '')
  sheetState.review = titleBreakdown.value?.review || null
}

function openCoverBreakdown() {
  if (!coverSourceText.value) {
    showToast('当前视频没有封面地址，无法拆解')
    return
  }
  sheetState.visible = true
  sheetState.mode = coverBreakdown.value ? 'edit' : 'create'
  sheetState.breakdownId = Number(coverBreakdown.value?.id || 0)
  sheetState.sourceType = 'cover'
  sheetState.sourceText = coverSourceText.value
  sheetState.sourceStartOffset = null
  sheetState.sourceEndOffset = null
  sheetState.breakdownType = 'cover'
  sheetState.enabledBreakdownTypes = resolveEnabledBreakdownTypes(sheetState.sourceType)
  sheetState.initialBreakdownContent = String(coverBreakdown.value?.breakdownContent || '')
  sheetState.review = coverBreakdown.value?.review || null
}

function handleTitleClick() {
  if (titleBreakdown.value) {
    openEditBreakdown(titleBreakdown.value)
  }
}

function openSelectionBreakdown(payload) {
  sheetState.visible = true
  sheetState.mode = 'create'
  sheetState.breakdownId = 0
  sheetState.sourceType = String(payload?.sourceType || 'subtitle_selection')
  sheetState.sourceText = String(payload?.sourceText || '')
  sheetState.sourceStartOffset = Number.isFinite(Number(payload?.sourceStartOffset))
    ? Number(payload.sourceStartOffset)
    : null
  sheetState.sourceEndOffset = Number.isFinite(Number(payload?.sourceEndOffset))
    ? Number(payload.sourceEndOffset)
    : null
  sheetState.breakdownType = String(payload?.breakdownType || resolveDefaultBreakdownType(sheetState.sourceType))
  sheetState.enabledBreakdownTypes = resolveEnabledBreakdownTypes(sheetState.sourceType)
  sheetState.initialBreakdownContent = ''
  sheetState.review = null
}

function openEditBreakdown(breakdown) {
  sheetState.visible = true
  sheetState.mode = 'edit'
  sheetState.breakdownId = Number(breakdown?.id || 0)
  sheetState.sourceType = String(breakdown?.sourceType || 'subtitle_selection')
  sheetState.sourceText = String(breakdown?.sourceText || '')
  sheetState.sourceStartOffset = Number.isFinite(Number(breakdown?.sourceStartOffset))
    ? Number(breakdown.sourceStartOffset)
    : null
  sheetState.sourceEndOffset = Number.isFinite(Number(breakdown?.sourceEndOffset))
    ? Number(breakdown.sourceEndOffset)
    : null
  sheetState.breakdownType = String(breakdown?.breakdownType || resolveDefaultBreakdownType(sheetState.sourceType))
  if (sheetState.sourceType === 'description_selection' && sheetState.breakdownType === 'body') {
    sheetState.breakdownType = 'description'
  }
  sheetState.enabledBreakdownTypes = resolveEnabledBreakdownTypes(sheetState.sourceType)
  sheetState.initialBreakdownContent = String(breakdown?.breakdownContent || '')
  sheetState.review = breakdown?.review || null
}

async function handleSaveBreakdown(payload) {
  if (!detail.value?.id) return
  const breakdownContent = String(payload?.breakdownContent || '').trim()
  if (!breakdownContent) {
    showToast('请输入拆解内容')
    return
  }

  sheetState.saving = true
  try {
    await saveYoutubeBreakdown({
      youtubeVideoId: Number(detail.value.id),
      breakdownId: Number(sheetState.breakdownId || 0) || undefined,
      sourceType: sheetState.sourceType,
      sourceText: sheetState.sourceText,
      sourceStartOffset: sheetState.sourceStartOffset,
      sourceEndOffset: sheetState.sourceEndOffset,
      breakdownType: String(payload?.breakdownType || sheetState.breakdownType || 'body'),
      breakdownContent,
    })
    showToast(sheetState.mode === 'edit' ? '拆解已更新' : '拆解已保存')
    resetSheetState()
    await refreshDetailPreservingScroll()
  } catch (saveError) {
    showToast(saveError?.message || '保存失败')
  } finally {
    sheetState.saving = false
  }
}

async function handleDeleteBreakdown() {
  if (!sheetState.breakdownId) return
  try {
    await showConfirmDialog({
      title: '确认删除拆解',
      message: '删除后不可恢复，是否继续？',
      confirmButtonText: '删除',
      confirmButtonColor: '#e11d48',
    })
  } catch (_) {
    return
  }

  sheetState.saving = true
  try {
    await deleteYoutubeBreakdown(Number(sheetState.breakdownId))
    showToast('拆解已删除')
    resetSheetState()
    await refreshDetailPreservingScroll()
  } catch (deleteError) {
    showToast(deleteError?.message || '删除失败')
  } finally {
    sheetState.saving = false
  }
}

function resetDomainForm() {
  domainForm.visible = false
  domainForm.saving = false
  domainForm.mode = 'create'
  domainForm.breakdownId = 0
  domainForm.breakdownContent = ''
}

function openDomainBreakdownSheet() {
  if (!hasDomainCategory.value) {
    showToast('请先在列表页设置领域分类')
    return
  }
  domainSheet.visible = true
  resetDomainForm()
}

function openDomainCreateForm() {
  resetDomainForm()
  domainForm.visible = true
  domainForm.mode = 'create'
}

function openDomainEditForm(item) {
  domainForm.visible = true
  domainForm.mode = 'edit'
  domainForm.breakdownId = Number(item?.id || 0)
  domainForm.breakdownContent = String(item?.breakdownContent || '')
}

async function saveDomainBreakdown() {
  const categoryId = Number(detail.value?.domainCategoryId || 0)
  if (!categoryId) {
    showToast('请先在列表页设置领域分类')
    return
  }
  const breakdownContent = String(domainForm.breakdownContent || '').trim()
  if (!breakdownContent) {
    showToast('请输入拆解内容')
    return
  }

  domainForm.saving = true
  try {
    await saveDomainCategoryBreakdown({
      categoryId,
      breakdownId: Number(domainForm.breakdownId || 0) || undefined,
      title: '',
      breakdownContent,
      sortNo: 0,
    })
    showToast(domainForm.mode === 'edit' ? '领域拆解已更新' : '领域拆解已保存')
    resetDomainForm()
    await refreshDetailPreservingScroll()
  } catch (error) {
    showToast(error?.message || '保存失败')
  } finally {
    domainForm.saving = false
  }
}

async function deleteDomainBreakdown(item) {
  const breakdownId = Number(item?.id || 0)
  if (!breakdownId) return
  try {
    await showConfirmDialog({
      title: '确认删除领域拆解',
      message: '删除后不可恢复，是否继续？',
      confirmButtonText: '删除',
      confirmButtonColor: '#e11d48',
    })
  } catch (_) {
    return
  }

  domainForm.saving = true
  try {
    await deleteDomainCategoryBreakdown(breakdownId)
    showToast('领域拆解已删除')
    resetDomainForm()
    await refreshDetailPreservingScroll()
  } catch (error) {
    showToast(error?.message || '删除失败')
  } finally {
    domainForm.saving = false
  }
}

function resetEssayForm() {
  essayForm.visible = false
  essayForm.saving = false
  essayForm.mode = 'create'
  essayForm.essayId = 0
  essayForm.content = ''
}

function openEssaySheet() {
  essaySheet.visible = true
  resetEssayForm()
}

function openEssayCreateForm() {
  resetEssayForm()
  essayForm.visible = true
  essayForm.mode = 'create'
}

function openEssayEditForm(item) {
  essayForm.visible = true
  essayForm.mode = 'edit'
  essayForm.essayId = Number(item?.id || 0)
  essayForm.content = String(item?.content || '')
}

async function saveEssay() {
  const content = String(essayForm.content || '').trim()
  if (!content) {
    showToast('请输入随笔内容')
    return
  }

  essayForm.saving = true
  try {
    await saveGlobalEssay({
      essayId: Number(essayForm.essayId || 0) || undefined,
      content,
    })
    showToast(essayForm.mode === 'edit' ? '随笔已更新' : '随笔已保存')
    resetEssayForm()
    await refreshDetailPreservingScroll()
  } catch (error) {
    showToast(error?.message || '保存失败')
  } finally {
    essayForm.saving = false
  }
}

async function deleteEssay(item) {
  const essayId = Number(item?.id || 0)
  if (!essayId) return
  try {
    await showConfirmDialog({
      title: '确认删除随笔',
      message: '删除后不可恢复，是否继续？',
      confirmButtonText: '删除',
      confirmButtonColor: '#e11d48',
    })
  } catch (_) {
    return
  }

  essayForm.saving = true
  try {
    await deleteGlobalEssay(essayId)
    showToast('随笔已删除')
    resetEssayForm()
    await refreshDetailPreservingScroll()
  } catch (error) {
    showToast(error?.message || '删除失败')
  } finally {
    essayForm.saving = false
  }
}
</script>

<style scoped>
.detail-page {
  min-height: 100vh;
  padding: calc(env(safe-area-inset-top) + 16px) 16px 28px;
  box-sizing: border-box;
}

.detail-back {
  position: fixed;
  top: calc(env(safe-area-inset-top) + 12px);
  left: 16px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.14);
  color: var(--yt-text);
  backdrop-filter: blur(16px);
  cursor: pointer;
}

.detail-hero {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: var(--yt-radius-xl);
  background: #fff;
  box-shadow: var(--yt-shadow);
}

.detail-hero__breakdown {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  min-width: 58px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
}

.detail-intro {
  margin-top: 18px;
}

.detail-intro__eyebrow {
  color: var(--yt-accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.detail-title-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 10px;
}

.detail-intro__title {
  margin: 10px 0 0;
  color: var(--yt-text);
  font-size: 28px;
  line-height: 1.25;
  letter-spacing: -0.05em;
}

.detail-intro__title--breakdown {
  text-decoration-line: underline;
  text-decoration-style: dashed;
  text-decoration-color: rgba(225, 29, 72, 0.82);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 5px;
}

.detail-title-row :deep(.van-button) {
  margin-top: 12px;
  min-width: 58px;
}

.detail-intro__subtitle {
  margin-top: 8px;
  color: var(--yt-muted);
  font-size: 14px;
  line-height: 1.7;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.meta-card,
.action-card,
.info-card,
.transcript-card,
.state-card {
  border: 1px solid var(--yt-line);
  border-radius: var(--yt-radius-xl);
  background: var(--yt-surface-strong);
  box-shadow: var(--yt-shadow);
}

.meta-card {
  padding: 16px;
}

.detail-action-grid {
  display: grid;
  gap: 12px;
  margin-top: 18px;
}

.action-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.action-card__body {
  min-width: 0;
}

.action-card__label {
  display: block;
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.action-card strong {
  display: block;
  margin-top: 9px;
  color: var(--yt-text);
  font-size: 18px;
  line-height: 1.3;
  word-break: break-word;
}

.action-card p {
  margin: 6px 0 0;
  color: var(--yt-muted);
  font-size: 13px;
  line-height: 1.5;
}

.meta-card__label,
.info-card__label,
.transcript-card__label {
  display: block;
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.meta-card strong {
  display: block;
  margin-top: 10px;
  color: var(--yt-text);
  font-size: 20px;
  line-height: 1.2;
}

.info-card,
.transcript-card {
  margin-top: 18px;
  padding: 20px 18px;
}

.info-card__content,
.transcript-card__content,
.transcript-card__empty,
.state-card__desc {
  margin-top: 14px;
  color: var(--yt-text);
  font-size: 15px;
  line-height: 1.9;
  white-space: pre-wrap;
  word-break: break-word;
}

.info-card__content :deep(.selectable-text__action),
.transcript-card__content :deep(.selectable-text__action) {
  white-space: nowrap;
}

.transcript-card__empty {
  color: var(--yt-muted);
}

.state-wrap {
  padding-top: 72px;
}

.state-card {
  padding: 28px 22px;
  text-align: center;
}

.state-card__title {
  color: var(--yt-text);
  font-size: 18px;
  font-weight: 700;
}

.text-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}

.text-sheet__body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 16px 8px;
  -webkit-overflow-scrolling: touch;
}

.text-sheet__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 0 0 16px 2px;
}

.text-sheet__header > div {
  min-width: 0;
}

.text-sheet__eyebrow,
.text-sheet__label {
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.text-sheet__header h2 {
  margin: 8px 0 0;
  color: var(--yt-text);
  font-size: 21px;
  line-height: 1.25;
  word-break: break-word;
}

.text-sheet__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.text-item {
  padding: 14px;
  border: 1px solid var(--yt-line);
  border-radius: var(--yt-radius-md);
  background: #f8fafc;
}

.text-item__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.text-item__header strong {
  color: var(--yt-text);
  font-size: 14px;
  line-height: 1.4;
}

.text-item__actions {
  display: flex;
  flex-shrink: 0;
  gap: 6px;
}

.text-item__content {
  margin-top: 10px;
  color: var(--yt-text);
  font-size: 14px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}

.text-sheet__form {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--yt-line);
}

.text-sheet__form .text-sheet__label {
  display: block;
  margin-bottom: 10px;
}

.text-sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px calc(env(safe-area-inset-bottom) + 16px);
  border-top: 1px solid var(--yt-line);
  background: #fff;
  flex-shrink: 0;
}

.text-sheet__actions .van-button {
  flex: 1;
}

@media (max-width: 360px) {
  .meta-grid {
    grid-template-columns: 1fr;
  }

  .detail-title-row {
    grid-template-columns: 1fr;
  }

  .detail-title-row :deep(.van-button) {
    justify-self: start;
  }

  .action-card {
    flex-direction: column;
  }

  .text-item__header {
    flex-direction: column;
  }
}
</style>
